import { IPosition, IPositionType } from 'src/generated-types'
import { IChangeType } from 'src/generated-types/project-version-changes'
import {
  SHAPE_TAGS,
  SVG_NAMESPACE,
  boundsOf,
  isAnchorCircle,
  markPosition,
  tagOf,
} from '../components/positions/configure/drawingSvgElements'
import { ACTION_ATTRIBUTE } from '../components/positions/configure/useDrawingSelection'
import { CHANGE_GLYPH } from './changeLabels'

// Set on the "+" markers drawn on corners while the Add line tool is on, with the corner's id.
export const ADD_LINE_ATTRIBUTE = 'data-revision-addline'
const BADGE_RADIUS = 9
const MARKER_RADIUS = 11
export const OTHER_TYPE_OPACITY = '0.26'
const GHOST_DASH = '6 4'

type Point = { x: number; y: number }

const svgNode = <K extends keyof SVGElementTagNameMap>(tag: K, attributes: Record<string, string | number>) => {
  const node = document.createElementNS(SVG_NAMESPACE, tag)
  Object.entries(attributes).forEach(([name, value]) => node.setAttribute(name, String(value)))
  return node
}

const centerOfCircle = (circle: Element): Point => ({
  x: Number(circle.getAttribute('cx')),
  y: Number(circle.getAttribute('cy')),
})

// Where a position's marker goes: a mooring line's anchor, otherwise the middle of its shape.
export const pointOf = (nodes: Element[], type: IPositionType): Point | null => {
  if (type === IPositionType.MooringLine) {
    const anchor = nodes.find(isAnchorCircle)
    if (anchor) return centerOfCircle(anchor)
  }
  const shape = nodes.find(node => SHAPE_TAGS.has(tagOf(node)) && !node.classList.contains('cfg-hit'))
  const box = shape && boundsOf(shape as SVGGraphicsElement)
  return box ? { x: box.x + box.width / 2, y: box.y + box.height / 2 } : null
}

const badgeOffset = (type: IPositionType) =>
  type === IPositionType.MooringLine || type === IPositionType.Corner ? { x: 11, y: -11 } : { x: 0, y: 0 }

// A coloured disc with the change's glyph: + added, ⇄ modified, ↗ moved, − removed.
export const appendBadge = (
  svg: SVGSVGElement,
  point: Point,
  type: IPositionType,
  kind: IChangeType,
  color: string,
  target: { attribute: string; value: string; positionType?: number } | null,
  dimmed: boolean,
) => {
  const offset = badgeOffset(type)
  const group = svgNode('g', { class: 'revision-badge', opacity: dimmed ? OTHER_TYPE_OPACITY : 1 })
  group.appendChild(
    svgNode('circle', {
      cx: point.x + offset.x,
      cy: point.y + offset.y,
      r: BADGE_RADIUS,
      fill: color,
      stroke: '#fff',
      'stroke-width': 1.5,
    }),
  )
  const text = svgNode('text', {
    x: point.x + offset.x,
    y: point.y + offset.y + 0.5,
    'font-size': 12,
    'font-weight': 700,
    fill: '#fff',
    'text-anchor': 'middle',
    'dominant-baseline': 'middle',
  })
  text.textContent = CHANGE_GLYPH[kind]
  group.appendChild(text)
  if (target && !dimmed) {
    group.setAttribute(target.attribute, target.value)
    if (target.positionType != null) group.setAttribute('data-cfg-type', String(target.positionType))
    else group.setAttribute(ACTION_ATTRIBUTE, '1')
    group.style.cursor = 'pointer'
  } else {
    group.setAttribute('pointer-events', 'none')
  }
  svg.appendChild(group)
}

interface BadgeOptions {
  positions: IPosition[]
  changeByName: Map<string, IChangeType>
  colors: Record<IChangeType, string>
  activeType?: IPositionType
}

// Badges for the draft's own changed positions; removed ones are badged where their ghost is drawn.
export const drawChangeBadges = (svg: SVGSVGElement, { positions, changeByName, colors, activeType }: BadgeOptions) => {
  positions.forEach(position => {
    const kind = changeByName.get(position.name ?? '')
    if (kind == null || kind === IChangeType.Removed || position.type === IPositionType.Cage) return
    const point = pointOf(markPosition(svg, position), position.type)
    if (!point) return
    const isActive = activeType == null || position.type === activeType
    appendBadge(
      svg,
      point,
      position.type,
      kind,
      colors[kind],
      { attribute: 'data-cfg-id', value: String(position.id), positionType: position.type },
      !isActive,
    )
  })
}

// "+" markers on the corners a mooring line can be added from.
export const drawAddLineMarkers = (svg: SVGSVGElement, corners: IPosition[], color: string) => {
  corners.forEach(corner => {
    const point = pointOf(markPosition(svg, corner), corner.type)
    if (!point) return
    const group = svgNode('g', { class: 'revision-addline' })
    group.setAttribute(ADD_LINE_ATTRIBUTE, String(corner.id))
    group.setAttribute(ACTION_ATTRIBUTE, '1')
    group.style.cursor = 'copy'
    group.appendChild(
      svgNode('circle', {
        cx: point.x,
        cy: point.y,
        r: MARKER_RADIUS,
        fill: color,
        stroke: '#fff',
        'stroke-width': 2,
      }),
    )
    const text = svgNode('text', {
      x: point.x,
      y: point.y + 1,
      'font-size': 17,
      'font-weight': 700,
      fill: '#fff',
      'text-anchor': 'middle',
      'dominant-baseline': 'middle',
    })
    text.textContent = '+'
    group.appendChild(text)
    svg.appendChild(group)
  })
}

// Frame corners on the outside of the grid, from their numbers (301 + column * (rows + 1) + row).
export const perimeterCorners = (corners: IPosition[], rows: number) => {
  if (rows <= 0) return corners
  const stride = rows + 1
  const indexOf = (corner: IPosition) => Number(corner.name) - 301
  const numbered = corners.filter(corner => Number.isFinite(indexOf(corner)) && indexOf(corner) >= 0)
  if (numbered.length !== corners.length) return corners
  const lastColumn = Math.max(...numbered.map(corner => Math.floor(indexOf(corner) / stride)))
  return numbered.filter(corner => {
    const index = indexOf(corner)
    const column = Math.floor(index / stride)
    const row = index % stride
    return column === 0 || column === lastColumn || row === 0 || row === rows
  })
}

// The published line of a moved mooring line, dashed, with an arrow from its old anchor to the new one.
export const drawMoveGhost = (
  svg: SVGSVGElement,
  baseNodes: Element[],
  newAnchor: Point | null,
  ghostColor: string,
  movedColor: string,
  offset: Point = { x: 0, y: 0 },
) => {
  const outermost = baseNodes.filter(node => !baseNodes.some(other => other !== node && other.contains(node)))
  const shapes = outermost.filter(node => tagOf(node) === 'line' || tagOf(node) === 'path' || isAnchorCircle(node))
  const group = svgNode('g', {
    class: 'revision-move-ghost',
    'pointer-events': 'none',
    transform: `translate(${offset.x} ${offset.y})`,
  })
  newAnchor = newAnchor && { x: newAnchor.x - offset.x, y: newAnchor.y - offset.y }
  shapes.forEach(node => {
    const clone = node.cloneNode(false) as SVGElement
    ;['id', 'data-position-id', 'data-cfg-id', 'data-cfg-type'].forEach(name => clone.removeAttribute(name))
    clone.setAttribute('stroke', ghostColor)
    clone.setAttribute('stroke-dasharray', GHOST_DASH)
    clone.setAttribute('fill', tagOf(clone) === 'circle' ? 'none' : clone.getAttribute('fill') ?? 'none')
    clone.setAttribute('opacity', '0.9')
    group.appendChild(clone)
  })
  const oldAnchorNode = baseNodes.find(isAnchorCircle)
  if (oldAnchorNode && newAnchor) {
    const from = centerOfCircle(oldAnchorNode)
    const length = Math.hypot(newAnchor.x - from.x, newAnchor.y - from.y)
    if (length > 12) {
      // Curved and dotted so the move never reads as a mooring line, which is always straight and solid.
      const ux = (newAnchor.x - from.x) / length
      const uy = (newAnchor.y - from.y) / length
      const start = { x: from.x + ux * 7, y: from.y + uy * 7 }
      const tip = { x: newAnchor.x - ux * 9, y: newAnchor.y - uy * 9 }
      const bend = Math.min(length * 0.25, 40)
      const control = { x: (start.x + tip.x) / 2 - uy * bend, y: (start.y + tip.y) / 2 + ux * bend }
      const tangentLength = Math.hypot(tip.x - control.x, tip.y - control.y) || 1
      const tx = (tip.x - control.x) / tangentLength
      const ty = (tip.y - control.y) / tangentLength
      const head = 7
      group.appendChild(
        svgNode('path', {
          d: `M${start.x} ${start.y}Q${control.x} ${control.y} ${tip.x} ${tip.y}`,
          stroke: movedColor,
          'stroke-width': 1.5,
          'stroke-dasharray': '1 4',
          'stroke-linecap': 'round',
          fill: 'none',
          opacity: 0.85,
        }),
      )
      group.appendChild(
        svgNode('path', {
          d: `M${tip.x - tx * head - ty * head * 0.6} ${tip.y - ty * head + tx * head * 0.6}L${tip.x} ${tip.y}L${
            tip.x - tx * head + ty * head * 0.6
          } ${tip.y - ty * head - tx * head * 0.6}`,
          stroke: movedColor,
          'stroke-width': 1.5,
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
          fill: 'none',
          opacity: 0.85,
        }),
      )
    }
  }
  svg.insertBefore(group, svg.firstChild)
}
